import { useId } from 'react'
import { palette } from '@versports/theme'

/** El único formato que acepta el backend (`AdminUpdateClubRequest._validate_hex`). */
export const HEX_RE = /^#[0-9A-Fa-f]{6}$/

/**
 * Normaliza lo que tipeó el operador al formato que guarda el backend: agrega
 * el `#` que falta y expande el atajo de 3 dígitos (`#abc` → `#aabbcc`). Se
 * corre en el blur, no en cada tecla, porque `#ab` camino a `#abcdef` no tiene
 * que convertirse en `#aabbaa` a mitad de palabra.
 *
 * Lo que no sea hex vuelve tal cual (recortado): así la validación de
 * `saveBranding` lo marca en vez de que el campo se coma un valor raro.
 */
export function normalizeHex(raw: string): string {
  const body = raw.trim().replace(/^#/, '')
  if (/^[0-9A-Fa-f]{3}$/.test(body)) return `#${[...body].map((c) => c + c).join('')}`
  if (/^[0-9A-Fa-f]{6}$/.test(body)) return `#${body}`
  return raw.trim()
}

export type ColorFieldProps = {
  /** Ej: "Color de marca". El "(hex)" lo agrega el campo. */
  label: string
  value: string
  onChange: (value: string) => void
  /** Qué pasa si el club no define este color — se dice abajo del campo. */
  emptyHint?: string
}

/**
 * Campo de color del panel de superadmin: swatch clickeable + hex, sincronizados.
 *
 * Por qué existe (mg, 2026-09-10: "que los selectores de colores muestren los
 * colores"): los tres colores de la card "Marca" eran un `<input type="text">`
 * donde había que tipear el hex de memoria, con un cuadradito de 24px que sólo
 * aparecía si el hex ya estaba completo. Acá el `<input type="color">` abre el
 * picker del sistema y muestra el color siempre; el texto sigue estando porque
 * el color de un club llega pegado desde su manual de marca, no elegido a ojo.
 *
 * El picker no sabe representar "sin color": su `value` es siempre un
 * `#rrggbb` válido. Cuando el valor está vacío o a medio tipear muestra el
 * tono del propio borde, marcado con un guión, y **no** lo propaga — un club sin color definido no
 * es lo mismo que uno con negro elegido, y el que guarda es el operador, no un
 * default que nadie eligió.
 */
export function ColorField({ label, value, onChange, emptyHint }: ColorFieldProps) {
  const id = useId()
  const raw = value.trim()
  const defined = HEX_RE.test(raw)

  return (
    <label className="flex flex-col gap-xs text-sm" htmlFor={`${id}-hex`}>
      {label} (hex)
      <div className="flex items-center gap-sm">
        <span className="relative inline-flex">
          <input
            type="color"
            aria-label={`${label}: elegir con el selector`}
            // `appearance-none` + los pseudo-elementos del swatch: sin esto el
            // browser pinta su propio marco gris, que no es el de los inputs.
            className={`h-10 w-10 shrink-0 cursor-pointer appearance-none rounded-sm border border-outline bg-transparent p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-0${
              defined ? '' : ' opacity-60'
            }`}
            value={defined ? raw.toLowerCase() : palette.surfaceHighlight}
            onChange={(e) => onChange(e.target.value)}
          />
          {!defined && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-text-muted"
            >
              —
            </span>
          )}
        </span>
        <input
          id={`${id}-hex`}
          type="text"
          className="input max-w-[160px]"
          placeholder="#E25A1B"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            const normalized = normalizeHex(value)
            if (normalized !== value) onChange(normalized)
          }}
        />
      </div>
      {!raw && (
        <span className="text-xs text-text-muted">
          Sin color definido.{emptyHint ? ` ${emptyHint}` : ''}
        </span>
      )}
      {raw && !defined && (
        <span className="text-xs text-danger">
          Formato inválido: tiene que ser #RRGGBB. Hasta que el hex esté completo el selector queda
          apagado y no se guarda nada.
        </span>
      )}
    </label>
  )
}
